import { CommonModule } from '@angular/common';
import {
    Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges,
} from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';

import { UserDataRecord as UserDataHeader } from '../../../api/user-data-service';
import { UserDataService } from '../../../services/user-data/user-data.service';
import { UserDataFilter } from '../../../services/user-data/user-data.filter';
import { Page, QuerySortDirection } from '@nucleus/ui-base';

/**
 * UserData entity-injection picker — the bridge that lets a task's JSON-schema
 * say {@code "x-options": { "source": "userdata", "filter": { "type": "NE" } }}
 * and have the schedule-creation form render a real searchable dropdown of
 * UserData rows of that type.
 *
 * Design decisions:
 *   • Single-select for now. Multi-select is a follow-up — most task schemas
 *     reference a single resource per execution.
 *   • Form value is the user_key string (the natural key on user_data). The
 *     full row is also exposed via {@code (selectionChange)} for callers that
 *     need the JSON payload at the time of schedule definition.
 *   • Autocomplete filter matches BOTH user_key AND label so typing the
 *     friendly name still resolves the row.
 *   • Page-size cap: 200 rows. UserData is unbounded in production; tasks
 *     that legitimately need to browse 10k NEs should use a dialog browser
 *     (a follow-up component). 200 covers every realistic per-tenant catalog
 *     size we have today and keeps the autocomplete responsive.
 */
@Component({
    selector: 'app-user-data-picker',
    standalone: true,
    imports: [
        CommonModule, ReactiveFormsModule,
        MatFormFieldModule, MatInputModule, MatAutocompleteModule, MatIconModule,
    ],
    templateUrl: './user-data-picker.component.html',
    styleUrl: './user-data-picker.component.css',
})
export class UserDataPickerComponent implements OnInit, OnChanges {

    /** Required. The user_data.type to filter by ("NE" / "PROBE" / "CIRCUIT" / ...). */
    @Input({ required: true }) type!: string;

    /** Field label. Defaults to the type itself ("NE", "PROBE", ...) so the
     *  consumer doesn't have to provide it for the simple case. */
    @Input() label = '';

    /** Pre-selected user_key — drives the initial display value. */
    @Input() value: string | null = null;

    @Input() placeholder = '';
    @Input() required = false;
    @Input() disabled = false;

    /** Caller hook for per-row data — emits the full UserDataHeader (or null
     *  when the user clears the field), not just the user_key. */
    @Output() selectionChange = new EventEmitter<UserDataHeader | null>();

    /** And the bare user_key, so callers can [(value)]-bind without unpacking. */
    @Output() valueChange = new EventEmitter<string | null>();

    private readonly userDataService = inject(UserDataService);

    readonly inputControl = new FormControl<string | null>(null);
    rows: UserDataHeader[] = [];
    filtered: UserDataHeader[] = [];
    loading = false;
    loadError: string | null = null;

    private static readonly PAGE_SIZE = 200;

    ngOnInit(): void {
        this.fetchRows();
        this.inputControl.valueChanges.subscribe(v => {
            const needle = (v ?? '').toString().toLowerCase();
            this.filtered = needle
                ? this.rows.filter(r =>
                    (r.key ?? '').toLowerCase().includes(needle) ||
                    (r.label ?? '').toLowerCase().includes(needle))
                : [...this.rows];
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['type'] && !changes['type'].firstChange) {
            this.fetchRows();
        }
        if (changes['required'] || changes['disabled']) {
            this.applyValidators();
        }
        if (changes['value']) {
            this.inputControl.setValue(this.value ?? '', { emitEvent: false });
        }
    }

    private applyValidators(): void {
        if (this.required) {
            this.inputControl.setValidators([Validators.required]);
        } else {
            this.inputControl.clearValidators();
        }
        if (this.disabled) {
            this.inputControl.disable({ emitEvent: false });
        } else {
            this.inputControl.enable({ emitEvent: false });
        }
        this.inputControl.updateValueAndValidity({ emitEvent: false });
    }

    private fetchRows(): void {
        if (!this.type) {
            this.rows = [];
            this.filtered = [];
            return;
        }
        const filter = new UserDataFilter();
        filter.dataTypes = [this.type];
        const page = new Page(
            UserDataPickerComponent.PAGE_SIZE,
            0,
            'key',
            QuerySortDirection.ASC,
        );
        this.loading = true;
        this.loadError = null;
        this.userDataService.filter({
            onStart: () => {},
            onSuccess: (result) => {
                this.rows = (result?.data ?? []).slice()
                    .sort((a, b) => (a.key ?? '').localeCompare(b.key ?? ''));
                this.filtered = [...this.rows];

                // If a pre-selected value was passed before the fetch
                // resolved, surface its label now.
                if (this.value) {
                    this.inputControl.setValue(this.value, { emitEvent: false });
                }
            },
            onError: (err: string) => {
                this.loadError = err || 'Failed to load.';
                this.rows = [];
                this.filtered = [];
            },
            onComplete: () => { this.loading = false; },
        }, filter, page);
    }

    onOptionSelected(event: MatAutocompleteSelectedEvent): void {
        const key = event.option.value as string;
        const row = this.rows.find(r => r.key === key) ?? null;
        this.value = key;
        this.selectionChange.emit(row);
        this.valueChange.emit(key);
    }

    /** "X" button — clears both the input + the emitted value. */
    onClear(): void {
        this.inputControl.setValue('');
        this.value = null;
        this.selectionChange.emit(null);
        this.valueChange.emit(null);
    }

    /** Display function for the autocomplete — when the user re-focuses an
     *  already-selected row we want the input to show the user_key (not the
     *  internal object). */
    displayFn = (key: string | null): string => key ?? '';

    /** UI label fallback. */
    effectiveLabel(): string {
        return this.label || this.type;
    }
}
